import { useCallback, useEffect, useRef, useState } from "react";

import { getMenuItems } from "services/menu/rest";
import {
    MenuItemListDto,
    MenuItemsCountsDto,
    MenuItemsListFilterDto,
} from "services/menu/restModels";

import { ITEMS_PAGE_SIZE } from "features/MenuEditor/constants";

const EMPTY_COUNTS: MenuItemsCountsDto = {
    allCount: 0,
    stopListCount: 0,
    withoutPhotoCount: 0,
};

export interface UseMenuItemsListParams {
    menuId: number;
    menuCategoryId: number | null;
    query: string;
    filter: MenuItemsListFilterDto;
}

export interface MenuItemsListState {
    items: MenuItemListDto[];
    counts: MenuItemsCountsDto;
    total: number;
    loading: boolean;
    loadingMore: boolean;
    error: boolean;
    hasMore: boolean;
    loadMore: () => void;
    reload: () => void;
    patchItem: (item: MenuItemListDto) => void;
    removeItem: (menuItemId: number) => void;
    // Счётчик чипа «В стоп-листе» после переключения тумблера — правим на месте,
    // сводка приходит только с первой страницей и перезапросом её не добрать.
    adjustStopListCount: (delta: number) => void;
}

/*
* Позиции текущей категории — постранично. Раньше экран получал все позиции меню внутри
* getMenuById; здесь грузится первая страница, остальное — по мере прокрутки.
* */
export const useMenuItemsList = (params: UseMenuItemsListParams): MenuItemsListState => {
    const { menuId, menuCategoryId, query, filter } = params;

    const [items, setItems] = useState<MenuItemListDto[]>([]);
    const [counts, setCounts] = useState<MenuItemsCountsDto>(EMPTY_COUNTS);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    // Номер актуального скоупа. Смена категории/фильтра/поиска его увеличивает, и ответы
    // прошлого скоупа отбрасываются: иначе медленный ответ по старой категории дорисовал бы
    // свои позиции поверх новых.
    const scopeRef = useRef<number>(0);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    const patchItem = useCallback((updated: MenuItemListDto) => {
        setItems((current) =>
            current.map((item) => (item.id === updated.id ? updated : item))
        );
    }, []);

    const adjustStopListCount = useCallback((delta: number) => {
        setCounts((current) => ({
            ...current,
            stopListCount: Math.max(0, current.stopListCount + delta),
        }));
    }, []);

    const removeItem = useCallback((menuItemId: number) => {
        setItems((current) => current.filter((item) => item.id !== menuItemId));
        setTotal((current) => Math.max(0, current - 1));
    }, []);

    useEffect(() => {
        const scope = scopeRef.current + 1;
        scopeRef.current = scope;

        setLoading(true);
        setError(false);

        getMenuItems({
            menuId,
            categoryId: menuCategoryId ?? undefined,
            query: query || undefined,
            filter,
            skip: 0,
            limit: ITEMS_PAGE_SIZE,
        })
            .then((page) => {
                if (scopeRef.current !== scope) return;

                if (page) {
                    setItems(page.items);
                    setTotal(page.total ?? 0);
                    setCounts(page.counts ?? EMPTY_COUNTS);
                } else {
                    setError(true);
                    setItems([]);
                    setTotal(0);
                    setCounts(EMPTY_COUNTS);
                }
            })
            .catch(() => {
                if (scopeRef.current !== scope) return;
                setError(true);
                setItems([]);
                setTotal(0);
                setCounts(EMPTY_COUNTS);
            })
            .finally(() => {
                if (scopeRef.current !== scope) return;
                setLoading(false);
            });
    }, [menuId, menuCategoryId, query, filter, reloadKey]);

    const loadMore = useCallback(() => {
        if (loading || loadingMore) return;

        // Скоуп не меняем — догрузка продолжает текущий, но проверяем его при ответе:
        // пользователь мог переключить категорию, пока страница летела.
        const scope = scopeRef.current;

        setLoadingMore(true);

        getMenuItems({
            menuId,
            categoryId: menuCategoryId ?? undefined,
            query: query || undefined,
            filter,
            skip: items.length,
            limit: ITEMS_PAGE_SIZE,
        })
            .then((page) => {
                if (scopeRef.current !== scope || !page) return;

                // total и counts на догрузке не приходят — они уже получены с первой
                // страницей и от прокрутки не меняются.
                setItems((current) => [...current, ...page.items]);
            })
            .catch(() => undefined)
            .finally(() => {
                if (scopeRef.current !== scope) return;
                setLoadingMore(false);
            });
    }, [items.length, loading, loadingMore, menuId, menuCategoryId, query, filter]);

    return {
        items,
        counts,
        total,
        loading,
        loadingMore,
        error,
        hasMore: items.length < total,
        loadMore,
        reload,
        patchItem,
        removeItem,
        adjustStopListCount,
    };
};
